import { useEffect, useMemo, useState } from "react";
import { foodsApi, setsApi, type Food, type MealSet } from "../api/client";
import { PageHeader } from "../components/PageHeader";

export function SetsPage() {
  const [sets, setSets] = useState<MealSet[]>([]);
  const [foods, setFoods] = useState<Food[]>([]);
  const [creating, setCreating] = useState(false);

  const reload = () =>
    Promise.all([setsApi.list(), foodsApi.list()]).then(([s, f]) => {
      setSets(s);
      setFoods(f);
    });

  useEffect(() => {
    reload();
  }, []);

  const onDelete = async (id: string) => {
    await setsApi.remove(id);
    reload();
  };

  return (
    <div className="page">
      <PageHeader
        title="セット管理"
        titleClassName="page-title-half"
        right={
          <button className="btn-pearl" onClick={() => setCreating((v) => !v)}>
            {creating ? "閉じる" : "新規作成"}
          </button>
        }
      />

      {creating && (
        <CreateSetForm
          foods={foods}
          onCreated={async () => {
            setCreating(false);
            await reload();
          }}
        />
      )}

      {sets.length === 0 && !creating ? (
        <p className="muted">セットがまだありません。</p>
      ) : (
        sets.map((s) => (
          <div className="list-item" key={s.id} style={{ flexDirection: "column", alignItems: "stretch" }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div className="list-item-main">
                <span className="list-item-title">{s.name}</span>
                <span className="list-item-sub">
                  {s.total_kcal} kcal · {s.items.length}品
                </span>
              </div>
              <button className="btn-link" onClick={() => onDelete(s.id)}>
                削除
              </button>
            </div>
            <ul style={{ margin: "8px 0 0", paddingLeft: 16, color: "var(--color-ink-muted-80)" }}>
              {s.items.map((it) => (
                <li key={it.id} style={{ fontSize: 14 }}>
                  {it.food_name}（{it.kcal} kcal）
                </li>
              ))}
            </ul>
          </div>
        ))
      )}
    </div>
  );
}

function CreateSetForm({
  foods,
  onCreated,
}: {
  foods: Food[];
  onCreated: () => Promise<void> | void;
}) {
  const [name, setName] = useState("");
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  const items = useMemo(
    () =>
      foods
        .filter((f) => picked.has(f.id))
        .map((f) => ({ food_id: f.id, food_name: f.name, kcal: f.kcal })),
    [foods, picked]
  );

  const total = items.reduce((s, it) => s + it.kcal, 0);

  const toggle = (id: string) => {
    const next = new Set(picked);
    next.has(id) ? next.delete(id) : next.add(id);
    setPicked(next);
  };

  return (
    <form
      style={{ marginBottom: "var(--space-lg)" }}
      onSubmit={async (e) => {
        e.preventDefault();
        if (!name.trim() || items.length === 0) return;
        setSaving(true);
        try {
          await setsApi.create({ name: name.trim(), items });
          setName("");
          setPicked(new Set());
          await onCreated();
        } finally {
          setSaving(false);
        }
      }}
    >
      <div className="field">
        <label htmlFor="set-name">セット名</label>
        <input id="set-name" value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <h2 className="section-title">食品を選ぶ（{items.length}品 / {total} kcal）</h2>
      <div className="grid-2">
        {foods.map((f) => {
          const on = picked.has(f.id);
          return (
            <button
              type="button"
              key={f.id}
              className="tile"
              onClick={() => toggle(f.id)}
              style={{
                outline: on ? "2px solid var(--color-primary)" : "none",
              }}
            >
              <div className="tile-name">{f.name}</div>
              <div className="tile-kcal">{f.kcal} kcal</div>
            </button>
          );
        })}
      </div>
      <button
        type="submit"
        className="btn-primary"
        disabled={saving || !name.trim() || items.length === 0}
        style={{ marginTop: "var(--space-md)" }}
      >
        セットを作成
      </button>
    </form>
  );
}
